import { alpha } from "@mui/material/styles";

/** Tarjeta principal de pestaña — mismo rollo que Fichaje */
export const ttContentCardSx = (
  theme,
  accentColor,
  { maxWidth, centered = false } = {},
) => {
  const accent = accentColor || theme.palette.primary.main;
  return {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    ...(maxWidth
      ? { maxWidth, ...(centered ? { mx: "auto" } : {}) }
      : {}),
    p: { xs: 1.5, sm: 2.5 },
    borderRadius: 3,
    border: "1px solid",
    borderColor: alpha(accent, 0.32),
    background: `linear-gradient(145deg, ${alpha(accent, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 55%)`,
    boxShadow: `0 8px 32px ${alpha(accent, 0.1)}`,
    [theme.breakpoints.up("md")]: {
      border: "none",
      boxShadow: "none",
      background: "transparent",
      p: 0,
      borderRadius: 0,
    },
  };
};

export const ttFilterFieldSx = (theme) => ({
  minWidth: 0,
  "& .MuiInputBase-root": {
    backgroundColor: alpha(theme.palette.background.paper, 0.98),
    borderRadius: 1.25,
    fontSize: "0.8125rem",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.divider, 0.72),
  },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.primary.main, 0.45),
  },
});

export const ttToolbarSx = (theme) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 0.75,
  alignItems: "center",
  p: 1.25,
  borderRadius: 2,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.65),
  bgcolor: alpha(theme.palette.background.paper, 0.55),
  mb: 2,
});

export const TT_TOOLBAR_HEIGHT = 40;

export const ttToolbarButtonSx = {
  height: TT_TOOLBAR_HEIGHT,
  minHeight: TT_TOOLBAR_HEIGHT,
  fontWeight: 700,
  borderRadius: 1.25,
  px: 1.5,
  flex: { xs: "1 1 auto", sm: "0 0 auto" },
  minWidth: { xs: "calc(50% - 6px)", sm: "auto" },
};

/** Campos de filtro con la misma altura que los botones del toolbar */
export const ttToolbarFieldSx = (theme) => ({
  ...ttFilterFieldSx(theme),
  "& .MuiInputBase-root": {
    ...ttFilterFieldSx(theme)["& .MuiInputBase-root"],
    height: TT_TOOLBAR_HEIGHT,
    boxSizing: "border-box",
  },
  "& .MuiInputLabel-root": {
    lineHeight: 1,
  },
});

export const ttPanelPaperSx = (theme) => ({
  borderRadius: 3,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.8),
  overflow: "hidden",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 28px rgba(0,0,0,0.22)"
      : "0 10px 36px rgba(15,23,42,0.06)",
});

/** Cabecera + tabs (fijas con layout flex en la página) */
export const ttStickyChromeSx = (theme) => ({
  ...ttPanelPaperSx(theme),
  flexShrink: 0,
  zIndex: 30,
  mb: 1.25,
  backdropFilter: "blur(12px)",
  bgcolor: alpha(theme.palette.background.paper, 0.96),
});

/** Panel único desktop — cabecera + tabs + contenido */
export const ttUnifiedPanelSx = (theme) => ({
  ...ttPanelPaperSx(theme),
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  mb: { xs: 0, md: 0 },
  [theme.breakpoints.up("md")]: {
    borderRadius: 2,
    borderColor: alpha(theme.palette.divider, 0.55),
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 20px 40px rgba(0,0,0,0.18)"
        : "0 12px 40px rgba(15, 23, 42, 0.05)",
  },
});

/** Fondo sutil de página en desktop */
export const ttPageOuterSx = (theme) => ({
  [theme.breakpoints.up("md")]: {
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${theme.palette.background.default} 40%)`
        : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${theme.palette.grey[50]} 35%, ${theme.palette.background.default} 100%)`,
  },
});

/** Cabecera compacta desktop */
export const ttDesktopHeaderSx = (theme, accent) => ({
  px: { xs: 1, sm: 1.5, md: 2.5 },
  py: { xs: 0.75, sm: 0.85, md: 1.35 },
  flexShrink: 0,
  borderBottom: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.45),
  position: "relative",
  [theme.breakpoints.up("md")]: {
    background: `linear-gradient(90deg, ${alpha(accent, 0.06)} 0%, transparent 50%)`,
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 12,
      bottom: 12,
      width: 3,
      borderRadius: "0 3px 3px 0",
      background: `linear-gradient(180deg, ${accent}, ${alpha(accent, 0.45)})`,
    },
  },
});

/** Área de contenido con scroll independiente */
export const ttScrollableContentSx = (theme) => ({
  ...ttPanelPaperSx(theme),
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  maxWidth: "100%",
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
});

/** Contenido scrollable dentro del panel unificado */
export const ttUnifiedScrollSx = (theme) => ({
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  p: { xs: 1.25, sm: 2 },
  pb: { xs: 10, sm: 2 },
  [theme.breakpoints.up("md")]: {
    p: 3,
    pb: 3,
    bgcolor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.background.paper, 0.35)
        : alpha(theme.palette.background.paper, 0.72),
  },
});

/** Bloque de formulario centrado dentro de la tarjeta */
export const ttFormBlockSx = {
  maxWidth: 520,
  mx: "auto",
  width: "100%",
};

export const ttTableHeadSx = (theme) => ({
  "& .MuiTableCell-head": {
    fontWeight: 800,
    fontSize: "0.72rem",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    bgcolor: alpha(theme.palette.background.paper, 0.65),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
  },
});

export const ttTableContainerSx = (theme) => ({
  borderRadius: 2,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.7),
  bgcolor: alpha(theme.palette.background.paper, 0.72),
  maxWidth: "100%",
  minWidth: 0,
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  ...ttTableHeadSx(theme),
});

export const ttResponsiveTableSx = {
  minWidth: 640,
};

/** Grid Historial — desktop */
export const ttHistoryTableContainerSx = (theme) => ({
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.55),
  bgcolor: theme.palette.background.paper,
  maxWidth: "100%",
  minWidth: 0,
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 10px 28px rgba(0,0,0,0.16)"
      : "0 6px 28px rgba(15, 23, 42, 0.04)",
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "0.62rem",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    bgcolor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.background.default, 0.92)
        : alpha(theme.palette.grey[50], 0.95),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
    py: 0.75,
    px: 1,
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  "& .MuiTableBody-root .MuiTableRow-root": {
    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.035),
    },
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    py: 0.85,
    px: 1.25,
    verticalAlign: "middle",
    fontSize: "0.8125rem",
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.28)}`,
  },
  "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
    borderBottom: 0,
  },
});

export const ttHistoryTableSx = {
  width: "100%",
};

export const ttHistoryPunchCellSx = (theme, color) => ({
  display: "inline-flex",
  flexDirection: "column",
  gap: 0.2,
  minWidth: 108,
  maxWidth: 148,
  p: 0.85,
  borderRadius: 1.5,
  bgcolor: alpha(color, 0.07),
  border: "1px solid",
  borderColor: alpha(color, 0.16),
});

export const ttHistoryActionBtnSx = (theme) => ({
  justifyContent: "flex-start",
  px: 1,
  py: 0.4,
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "none",
  borderRadius: 1.25,
  borderColor: alpha(theme.palette.primary.main, 0.28),
  color: theme.palette.primary.dark,
  "&:hover": {
    bgcolor: alpha(theme.palette.primary.main, 0.06),
    borderColor: alpha(theme.palette.primary.main, 0.45),
  },
  "& .MuiButton-startIcon": {
    marginRight: 0.5,
    "& > *:nth-of-type(1)": { fontSize: "0.85rem" },
  },
});

export const ttMobileSessionCardSx = (theme, accentColor) => {
  const accent = accentColor || theme.palette.primary.main;
  return {
    position: "relative",
    overflow: "hidden",
    borderRadius: 2.5,
    border: "1px solid",
    borderColor: alpha(accent, 0.24),
    background: `linear-gradient(145deg, ${alpha(accent, 0.09)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 58%)`,
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 6px 24px ${alpha(theme.palette.common.black, 0.28)}`
        : `0 6px 24px ${alpha(theme.palette.common.black, 0.05)}`,
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 4,
      bgcolor: accent,
    },
  };
};

export const ttMobilePunchBlockSx = (theme, color) => ({
  flex: 1,
  minWidth: 0,
  p: 1.15,
  borderRadius: 2,
  bgcolor: alpha(color, 0.07),
  border: "1px solid",
  borderColor: alpha(color, 0.2),
});

export const ttMobileActionBtnSx = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.78rem",
  borderRadius: 1.5,
  py: 0.85,
};

export const ttMobileAvatarSx = (theme, color) => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: "0.72rem",
  color: color,
  bgcolor: alpha(color, 0.14),
  border: "2px solid",
  borderColor: alpha(color, 0.28),
});

export const ttTabsSx = (theme) => {
  const accent = theme.palette.primary.main;
  return {
    px: { xs: 0.75, sm: 1.25 },
    py: 0.5,
    minHeight: 46,
    background: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.35)} 100%)`,
    borderBottom: "1px solid",
    borderColor: alpha(theme.palette.divider, 0.45),
    "& .MuiTabs-scrollButtons": {
      borderRadius: 1.5,
      width: 32,
      "&.Mui-disabled": { opacity: 0.28 },
    },
    "& .MuiTabs-flexContainer": {
      gap: 0.5,
    },
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTab-root": {
      fontWeight: 700,
      minHeight: { xs: 36, sm: 40 },
      minWidth: { xs: "auto", sm: 90 },
      py: 0.75,
      px: { xs: 0.85, sm: 1.6 },
      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
      textTransform: "none",
      color: theme.palette.text.secondary,
      borderRadius: 2,
      border: "1px solid transparent",
      transition: "background-color 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, color 0.18s ease",
      "& .MuiTab-iconWrapper": {
        fontSize: "1.05rem",
        marginRight: "6px !important",
        transition: "color 0.18s ease",
      },
      "&:hover": {
        bgcolor: alpha(accent, 0.07),
        borderColor: alpha(accent, 0.14),
        color: theme.palette.text.primary,
      },
    },
    "& .MuiTab-root.Mui-selected": {
      color: theme.palette.primary.dark,
      fontWeight: 800,
      bgcolor: alpha(theme.palette.background.paper, 0.96),
      borderColor: alpha(accent, 0.32),
      boxShadow: `0 4px 16px ${alpha(accent, 0.16)}`,
      "& .MuiTab-iconWrapper": {
        color: accent,
      },
      "&:hover": {
        bgcolor: alpha(theme.palette.background.paper, 0.98),
      },
    },
  };
};

/** Tabs con indicador — desktop */
export const ttTabsDesktopSx = (theme) => {
  const accent = theme.palette.primary.main;
  return {
    px: 2.5,
    minHeight: 50,
    flexShrink: 0,
    bgcolor: alpha(theme.palette.background.paper, 0.55),
    borderBottom: "1px solid",
    borderColor: alpha(theme.palette.divider, 0.4),
    "& .MuiTabs-scrollButtons": {
      width: 28,
      borderRadius: 1,
      "&.Mui-disabled": { opacity: 0.25 },
    },
    "& .MuiTabs-flexContainer": {
      gap: 0.25,
    },
    "& .MuiTabs-indicator": {
      height: 2,
      borderRadius: "2px 2px 0 0",
      bgcolor: accent,
    },
    "& .MuiTab-root": {
      minHeight: 50,
      minWidth: 96,
      px: 1.75,
      py: 1,
      fontSize: "0.8125rem",
      fontWeight: 600,
      textTransform: "none",
      color: theme.palette.text.secondary,
      borderRadius: 0,
      border: "none",
      boxShadow: "none",
      transition: "color 0.15s ease, background-color 0.15s ease",
      "& .MuiTab-iconWrapper": {
        fontSize: "1.05rem",
        marginRight: "7px !important",
        opacity: 0.7,
      },
      "&:hover": {
        color: theme.palette.text.primary,
        bgcolor: alpha(accent, 0.05),
      },
    },
    "& .MuiTab-root.Mui-selected": {
      color: theme.palette.primary.dark,
      fontWeight: 700,
      bgcolor: "transparent",
      boxShadow: "none",
      "& .MuiTab-iconWrapper": {
        opacity: 1,
        color: accent,
      },
    },
  };
};

/** Panel lateral en fichaje desktop */
export const ttDesktopSidePanelSx = (theme) => ({
  [theme.breakpoints.up("md")]: {
    p: 2.5,
    borderRadius: 2,
    border: "1px solid",
    borderColor: alpha(theme.palette.divider, 0.5),
    bgcolor: alpha(theme.palette.background.paper, 0.85),
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.03)}`,
  },
});

export const ttKpiChipSx = {
  height: 24,
  fontWeight: 700,
  fontSize: "0.68rem",
};

export const ttSectionTitleSx = {
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  fontSize: "0.72rem",
  color: "text.secondary",
  mb: 1.25,
};

/** Anillo de estado del reloj de fichaje */
export const ttClockStatusRingSx = (theme, { statusColor, pulsing = false } = {}) => {
  const color = statusColor || theme.palette.text.disabled;
  return {
    position: "relative",
    width: { xs: 136, sm: 152 },
    height: { xs: 136, sm: 152 },
    borderRadius: "50%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    bgcolor: alpha(color, 0.08),
    border: "3px solid",
    borderColor: alpha(color, 0.38),
    boxShadow: `0 0 0 10px ${alpha(color, 0.06)}`,
    transition:
      "border-color 0.35s ease, box-shadow 0.35s ease, background-color 0.35s ease",
    ...(pulsing
      ? {
          animation: "ttClockPulse 2.6s ease-in-out infinite",
          "@keyframes ttClockPulse": {
            "0%, 100%": {
              boxShadow: `0 0 0 10px ${alpha(color, 0.06)}`,
            },
            "50%": {
              boxShadow: `0 0 0 16px ${alpha(color, 0.11)}`,
            },
          },
        }
      : {}),
  };
};

/** Reloj interactivo de fichaje (círculo = entrada / salida / fin pausa) */
export const ttClockPunchButtonSx = (theme, { accentColor, pulsing = false } = {}) => {
  const color = accentColor || theme.palette.text.disabled;
  return {
    ...ttClockStatusRingSx(theme, { statusColor: color, pulsing }),
    width: { xs: 168, sm: 184 },
    height: { xs: 168, sm: 184 },
    cursor: "pointer",
    border: "4px solid",
    borderColor: color,
    bgcolor: alpha(color, 0.12),
    boxShadow: `0 8px 32px ${alpha(color, 0.22)}`,
    transition:
      "transform 0.15s ease, box-shadow 0.25s ease, border-color 0.25s ease, background-color 0.25s ease",
    "&:hover": {
      bgcolor: alpha(color, 0.18),
      transform: "translateY(-2px)",
      boxShadow: `0 12px 36px ${alpha(color, 0.3)}`,
    },
    "&:active": {
      transform: "translateY(0) scale(0.98)",
    },
    "&.Mui-disabled": {
      opacity: 0.55,
      cursor: "default",
      transform: "none",
      boxShadow: `0 8px 32px ${alpha(color, 0.12)}`,
    },
  };
};

/** Botón secundario de pausa bajo el reloj */
export const ttClockPauseBtnSx = (theme) => ({
  mt: 0.5,
  px: 2.25,
  py: 0.65,
  fontWeight: 800,
  fontSize: "0.8125rem",
  textTransform: "none",
  borderRadius: 99,
  border: "2px solid",
  borderColor: alpha(theme.palette.warning.main, 0.55),
  color: theme.palette.warning.dark,
  bgcolor: alpha(theme.palette.warning.main, 0.08),
  "&:hover": {
    bgcolor: alpha(theme.palette.warning.main, 0.16),
    borderColor: theme.palette.warning.main,
  },
});

/** Botón de fichaje tipo tile */
export const ttPunchTileSx = (theme, color) => ({
  py: 1.5,
  px: 2,
  fontWeight: 800,
  borderRadius: 2.5,
  fontSize: "0.95rem",
  textTransform: "none",
  boxShadow: `0 6px 22px ${alpha(color, 0.28)}`,
  "&:hover": {
    boxShadow: `0 8px 28px ${alpha(color, 0.36)}`,
  },
  "&.Mui-disabled": {
    opacity: 0.55,
  },
});
